<template>
  <div class="min-h-screen bg-gray-50 dark:bg-slate-900">
    <div class="max-w-4xl mx-auto px-4 py-6">
      <!-- Modern Header with Gradient -->
      <div class="relative overflow-hidden rounded-2xl mb-8" style="background: linear-gradient(135deg, #06B6D4 0%, #0891B2 50%, #0E7490 100%);">
        <!-- Decorative Background Elements -->
        <div class="absolute inset-0 opacity-10">
          <div class="absolute top-4 right-4 w-32 h-32 rounded-full bg-white/20"></div>
          <div class="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10"></div>
          <div class="absolute bottom-4 left-4 w-20 h-20 rounded-full bg-white/15"></div>
        </div>
        
        <div class="relative p-8">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white">{{ directT('settings.accountSettings.accountSettings') }}</h1>
              <p class="text-white/90 text-lg font-medium">{{ directT('settings.accountSettings.managePersonalInfo') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="mb-8">
        <SettingsNav />
      </div>

      <!-- Modern Profile Information Card -->
      <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-slate-700">
        <div class="p-6">
          <!-- Profile Section Header -->
          <div class="flex items-center gap-3 mb-6">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900/20 dark:to-teal-900/20 flex items-center justify-center shadow-inner">
              <svg class="w-5 h-5 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <div>
              <h2 class="text-xl font-semibold text-slate-900 dark:text-white">{{ directT('settings.accountSettings.profile') }}</h2>
              <p class="text-slate-600 dark:text-slate-300">{{ directT('settings.accountSettings.updatePhotoAndDetails') }}</p>
            </div>
          </div>

          <form class="space-y-8">
            <!-- Basic Information Grid -->
            <div class="space-y-6">
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">{{ directT('settings.accountSettings.basicInformation') }}</h3>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ directT('settings.accountSettings.firstName') }}
                  </label>
                  <input
                    type="text"
                    v-model="profileData.first_name"
                    class="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                    placeholder="Enter your first name"
                  />
                </div>

                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ directT('settings.accountSettings.lastName') }}
                  </label>
                  <input
                    type="text"
                    v-model="profileData.last_name"
                    class="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                    placeholder="Enter your last name"
                  />
                </div>

                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ directT('settings.accountSettings.emailAddress') }}
                  </label>
                  <input
                    type="email"
                    v-model="profileData.email"
                    disabled
                    class="w-full px-4 py-3 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl text-slate-500 dark:text-slate-400 cursor-not-allowed"
                  />
                  <p class="text-sm text-slate-500 dark:text-slate-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg border border-amber-200 dark:border-amber-800">
                    <svg class="w-4 h-4 inline mr-2 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                    {{ directT('settings.accountSettings.emailCannotBeChanged') }}
                  </p>
                </div>

                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ directT('settings.accountSettings.phoneNumber') }}
                  </label>
                  <input
                    type="tel"
                    v-model="profileData.phone"
                    class="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div class="md:col-span-2 space-y-2">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {{ directT('settings.accountSettings.location') }}
                  </label>
                  <input
                    type="text"
                    v-model="profileData.location"
                    class="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-slate-900 dark:text-white transition-all duration-200 shadow-sm placeholder:text-slate-400"
                    :placeholder="directT('settings.accountSettings.enterYourLocation')"
                  />
                </div>
              </div>
            </div>

                         <!-- Profile Picture Section -->
             <div class="space-y-4">
               <div class="flex items-center gap-3">
                 <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center">
                   <svg class="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                   </svg>
                 </div>
                 <div>
                   <h3 class="text-lg font-semibold text-slate-900 dark:text-white">{{ directT('settings.accountSettings.profileInfo') }}</h3>
                   <p class="text-slate-600 dark:text-slate-300">{{ directT('settings.accountSettings.displayedProfile') }}</p>
                 </div>
               </div>

               <div class="flex items-center gap-6 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-600">
                 <div class="flex-shrink-0">
                   <div v-if="profileData.profile_picture_link" class="relative">
                     <img 
                       :src="profileData.profile_picture_link" 
                       alt="Profile Picture" 
                       class="w-16 h-16 object-cover rounded-xl border-2 border-white dark:border-slate-700 shadow-sm"
                     />
                     <div class="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white dark:border-slate-700 flex items-center justify-center">
                       <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                       </svg>
                     </div>
                   </div>
                   <div v-else class="w-16 h-16 bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900/30 dark:to-teal-900/30 rounded-xl flex items-center justify-center">
                     <svg class="w-8 h-8 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                     </svg>
                   </div>
                 </div>

                 <div class="flex-grow">
                   <p class="text-sm font-medium text-slate-900 dark:text-white mb-1">{{ directT('settings.accountSettings.profilePicture') }}</p>
                   <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">{{ directT('settings.accountSettings.fileTypeSize') }}</p>
                   
                   <input
                     type="file"
                     id="fileInput"
                     class="hidden"
                     @change="handleProfilePictureUpload"
                     accept="image/*"
                   />
                   <label 
                     for="fileInput"
                     class="choose-file-btn inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md"
                   >
                     <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                     </svg>
                     {{ directT('settings.accountSettings.chooseFile') }}
                   </label>
                 </div>
               </div>

               <div v-if="uploadStatus" class="p-3 rounded-lg border" :class="{
                 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800': uploadStatus === 'success',
                 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800': uploadStatus === 'error'
               }">
                 <div class="flex items-center gap-2">
                   <svg v-if="uploadStatus === 'success'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                   </svg>
                   <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                   </svg>
                   <span class="text-sm font-medium">{{ uploadStatusMessage }}</span>
                 </div>
               </div>
             </div>

                         <!-- Action Buttons -->
             <div class="flex items-center gap-4 pt-6 border-t border-gray-200 dark:border-slate-600">
               <button
                 type="button"
                 @click="resetForm"
                 class="px-6 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-all duration-200"
               >
                 {{ directT('settings.accountSettings.cancel') }}
               </button>

               <button
                 type="button"
                 @click="updateProfile"
                 :disabled="isLoading"
                 class="update-profile-btn px-6 py-3 text-sm font-medium text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm hover:shadow-md"
               >
                 <svg v-if="!isLoading" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                 </svg>
                 <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                   <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                   <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                 </svg>
                 {{ isLoading ? directT('settings.accountSettings.updating') : directT('settings.accountSettings.updateProfile') }}
               </button>
             </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import SettingsNav from "../SettingsNav.vue";
import { supabase } from '@/utils/supabase';
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

type TranslationRecord = Record<string, any>;
type TranslationsType = Record<string, TranslationRecord>;

const allTranslations: TranslationsType = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations,
};

export default defineComponent({
  name: "AccountSettings",
  components: {
    SettingsNav,
  },
  setup() {
    const { t, locale } = useI18n();

    // Profile data
    const profileData = ref({
      id: '',
      user_id: '',
      display_name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      location: '',
      role: '',
      profile_picture_link: '',
      country: '',
      birth_date: '',
      gender: '',
      skills: '',
      profession: '',
      company_name: '',
      company_website: '',
      bio: '',
      facebook: '',
      twitter: '',
      linkedin: '',
      youtube: ''
    });
    
    const isLoading = ref(false);
    const uploadStatus = ref('');
    const uploadStatusMessage = ref('');

    // Direct translation function with fallback
    function directT(key: string): string {
      const parts = key.split('.');
      let result: any = allTranslations[locale.value] || allTranslations.en;
      
      for (const part of parts) {
        if (result && typeof result === 'object' && part in result) {
          result = result[part];
        } else {
          // Fallback to English if key doesn't exist
          let englishResult = allTranslations.en;
          for (const engPart of parts) {
            if (englishResult && typeof englishResult === 'object' && engPart in englishResult) {
              englishResult = englishResult[engPart];
            } else {
              return key; // Return the key itself if not found in English either
            }
          }
          return typeof englishResult === 'string' ? englishResult : key;
        }
      }
      
      return typeof result === 'string' ? result : key;
    }

    // פונקציה לטעינת נתוני המשתמש
    const loadUserProfile = async () => {
      try {
        // קבלת המשתמש הנוכחי
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
          // קבלת פרטי הפרופיל מהמסד נתונים
          const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', session.user.id)
            .single();
          
          if (error) {
            console.error('שגיאה בטעינת פרופיל המשתמש:', error);
            return;
          }
          
          // אתחול הנתונים מהמסד נתונים
          if (data) {
            // פיצול display_name לשם פרטי ושם משפחה אם קיים
            const nameParts = (data as any).display_name ? (data as any).display_name.split(' ') : ['', ''];
            
            profileData.value = {
              ...profileData.value,
              ...data,
              first_name: nameParts[0] || '',
              last_name: nameParts.slice(1).join(' ') || '',
              email: session.user.email || '',
              // location שמתאים במקום address
              location: (data as any).location || ''
            };
          }
        }
      } catch (error) {
        console.error('שגיאה בטעינת נתוני המשתמש:', error);
      }
    };

    // פונקציה לטיפול בהעלאת תמונת פרופיל
    const handleProfilePictureUpload = async (event: Event) => {
      const fileInput = event.target as HTMLInputElement;
      const file = fileInput.files?.[0];
      
      if (!file) return;
      
      try {
        isLoading.value = true;
        uploadStatus.value = '';
        uploadStatusMessage.value = '';
        
        // בדיקה שהקובץ הוא תמונה
        if (!file.type.startsWith('image/')) {
          uploadStatus.value = 'error';
          uploadStatusMessage.value = 'יש להעלות קובץ תמונה בלבד';
          return;
        }
        
        // הגבלת גודל הקובץ (5MB)
        if (file.size > 5 * 1024 * 1024) {
          uploadStatus.value = 'error';
          uploadStatusMessage.value = 'גודל הקובץ לא יכול לעלות על 5MB';
          return;
        }
        
        // יצירת שם קובץ ייחודי באמצעות תאריך ושם המקורי
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !session.user) {
          uploadStatus.value = 'error';
          uploadStatusMessage.value = 'אתה לא מחובר למערכת';
          return;
        }
        
        const fileExt = file.name.split('.').pop();
        const fileName = `${session.user.id}_${Date.now()}.${fileExt}`;
        
        // העלאת הקובץ ל- Storage של Supabase
        const { data, error } = await supabase.storage
          .from('profilepictures')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: true
          });
        
        if (error) {
          console.error('שגיאה בהעלאת התמונה:', error);
          uploadStatus.value = 'error';
          uploadStatusMessage.value = 'שגיאה בהעלאת התמונה: ' + error.message;
          return;
        }
        
        // יצירת ה-URL הציבורי לתמונה
        const { data: { publicUrl } } = supabase.storage
          .from('profilepictures')
          .getPublicUrl(fileName);
        
        // עדכון הפרופיל עם הקישור החדש
        profileData.value.profile_picture_link = publicUrl;
        
        // עדכון בטבלת user_profiles - רק את העמודה profile_picture_link
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            profile_picture_link: publicUrl
            // כל שדה אחר לא יעודכן כאן
          })
          .eq('user_id', session.user.id);
        
        if (updateError) {
          console.error('שגיאה בעדכון פרטי המשתמש:', updateError);
          uploadStatus.value = 'error';
          uploadStatusMessage.value = 'שגיאה בעדכון פרטי המשתמש: ' + updateError.message;
          return;
        }
        
        uploadStatus.value = 'success';
        uploadStatusMessage.value = 'התמונה הועלתה בהצלחה!';
      } catch (error) {
        console.error('שגיאה לא צפויה:', error);
        uploadStatus.value = 'error';
        uploadStatusMessage.value = 'שגיאה לא צפויה בהעלאת התמונה';
      } finally {
        isLoading.value = false;
      }
    };

    // פונקציה לעדכון פרופיל המשתמש
    const updateProfile = async () => {
      try {
        isLoading.value = true;
        
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !session.user) {
          console.error('המשתמש לא מחובר');
          return;
        }
        
        // יצירת display_name משם פרטי ומשפחה
        const displayName = `${profileData.value.first_name} ${profileData.value.last_name}`.trim();
        
        // עדכון הפרופיל במסד הנתונים - רק עמודות שקיימות בטבלה
        const { error } = await supabase
          .from('user_profiles')
          .update({
            display_name: displayName,
            phone: profileData.value.phone,
            location: profileData.value.location, // במקום address
            // לא כולל שדות שאינם קיימים בטבלה
          })
          .eq('user_id', session.user.id);
        
        if (error) {
          console.error('שגיאה בעדכון הפרופיל:', error);
          uploadStatus.value = 'error';
          uploadStatusMessage.value = 'שגיאה בעדכון הפרופיל: ' + error.message;
          return;
        }
        
        uploadStatus.value = 'success';
        uploadStatusMessage.value = 'הפרופיל עודכן בהצלחה!';
      } catch (error) {
        console.error('שגיאה לא צפויה:', error);
        uploadStatus.value = 'error';
        uploadStatusMessage.value = 'שגיאה לא צפויה בעדכון הפרופיל';
      } finally {
        isLoading.value = false;
      }
    };

    // איפוס הטופס
    const resetForm = () => {
      loadUserProfile();
      uploadStatus.value = '';
      uploadStatusMessage.value = '';
    };
    
    // טעינת נתוני המשתמש בעת טעינת הקומפוננט
    onMounted(() => {
      loadUserProfile();
    });
    
    return {
      profileData,
      isLoading,
      uploadStatus,
      uploadStatusMessage,
      directT,
      handleProfilePictureUpload,
      updateProfile,
      resetForm
    };
  }
});
</script>

<style scoped>
.choose-file-btn {
  background-color: #06B6D4 !important;
}

.choose-file-btn:hover {
  background-color: #0891B2 !important;
}

.update-profile-btn {
  background-color: #06B6D4 !important;
}

.update-profile-btn:hover:not(:disabled) {
  background-color: #0891B2 !important;
}

/* Dark mode support */
.dark label {
  color: #f1f5f9 !important;
}

.dark .form-group label {
  color: #f1f5f9 !important;
}

.dark .color-control label {
  color: #f1f5f9 !important;
}

.dark input[type="text"],
.dark input[type="email"],
.dark input[type="tel"],
.dark input[type="number"],
.dark input[type="date"],
.dark input[type="time"],
.dark input[type="password"],
.dark input[type="file"],
.dark select,
.dark textarea {
  background-color: #475569 !important;
  border-color: #64748b !important;
  color: #f1f5f9 !important;
}

.dark input[type="text"]:focus,
.dark input[type="email"]:focus,
.dark input[type="tel"]:focus,
.dark input[type="number"]:focus,
.dark input[type="date"]:focus,
.dark input[type="time"]:focus,
.dark input[type="password"]:focus,
.dark input[type="file"]:focus,
.dark select:focus,
.dark textarea:focus {
  border-color: #06B6D4 !important;
  box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1) !important;
}

.dark input[type="text"]::placeholder,
.dark input[type="email"]::placeholder,
.dark input[type="tel"]::placeholder,
.dark input[type="number"]::placeholder,
.dark textarea::placeholder {
  color: #94a3b8 !important;
}

.dark select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23f1f5f9' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.5rem center !important;
  background-size: 1.5em 1.5em !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  padding-right: 2.5rem !important;
}

.dark input:disabled,
.dark select:disabled,
.dark textarea:disabled {
  background-color: #374151 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
}

/* General dark mode overrides */
.dark .bg-white {
  background-color: #1e293b !important;
}

.dark .bg-gray-50 {
  background-color: #374151 !important;
}

.dark .bg-gray-100 {
  background-color: #475569 !important;
}

.dark .border-gray-100 {
  border-color: #374151 !important;
}

.dark .border-gray-200 {
  border-color: #475569 !important;
}

.dark .border-gray-300 {
  border-color: #64748b !important;
}

.dark .text-slate-900 {
  color: #f1f5f9 !important;
}

.dark .text-slate-800 {
  color: #f1f5f9 !important;
}

.dark .text-slate-700 {
  color: #e2e8f0 !important;
}

.dark .text-slate-600 {
  color: #cbd5e1 !important;
}

.dark .text-slate-500 {
  color: #94a3b8 !important;
}

.dark .text-slate-400 {
  color: #64748b !important;
}

.dark .text-slate-300 {
  color: #cbd5e1 !important;
}

.dark .text-gray-900 {
  color: #f1f5f9 !important;
}

.dark .text-gray-800 {
  color: #f1f5f9 !important;
}

.dark .text-gray-700 {
  color: #e2e8f0 !important;
}

.dark .text-gray-600 {
  color: #cbd5e1 !important;
}

.dark .text-gray-500 {
  color: #94a3b8 !important;
}

.dark .text-gray-400 {
  color: #64748b !important;
}

/* Card backgrounds */
.dark .bg-slate-800 {
  background-color: #1e293b !important;
}

.dark .bg-slate-900 {
  background-color: #0f172a !important;
}

.dark .bg-slate-700 {
  background-color: #334155 !important;
}

.dark .bg-slate-600 {
  background-color: #475569 !important;
}

/* Border colors */
.dark .border-slate-700 {
  border-color: #334155 !important;
}

.dark .border-slate-600 {
  border-color: #475569 !important;
}

/* Gradient backgrounds for icons */
.dark .from-cyan-100 {
  --tw-gradient-from: rgba(6, 182, 212, 0.2) !important;
}

.dark .to-teal-100 {
  --tw-gradient-to: rgba(20, 184, 166, 0.2) !important;
}

.dark .from-purple-100 {
  --tw-gradient-from: rgba(168, 85, 247, 0.2) !important;
}

.dark .to-pink-100 {
  --tw-gradient-to: rgba(236, 72, 153, 0.2) !important;
}

.dark .from-cyan-900\/30 {
  --tw-gradient-from: rgba(22, 78, 99, 0.3) !important;
}

.dark .to-teal-900\/30 {
  --tw-gradient-to: rgba(19, 78, 74, 0.3) !important;
}

/* Icon colors */
.dark .text-cyan-600 {
  color: #06b6d4 !important;
}

.dark .text-cyan-400 {
  color: #22d3ee !important;
}

.dark .text-purple-600 {
  color: #a855f7 !important;
}

.dark .text-purple-400 {
  color: #c084fc !important;
}

/* Shadow adjustments for dark mode */
.dark .shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-sm {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.dark .shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.2) !important;
}

/* Hover effects for dark mode */
.dark .hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
}

.dark .hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
}

.dark .hover\:bg-gray-200:hover {
  background-color: #475569 !important;
}

.dark .hover\:bg-slate-600:hover {
  background-color: #475569 !important;
}

/* Status message backgrounds */
.dark .bg-emerald-50 {
  background-color: rgba(16, 185, 129, 0.1) !important;
}

.dark .bg-red-50 {
  background-color: rgba(239, 68, 68, 0.1) !important;
}

.dark .bg-amber-50 {
  background-color: rgba(245, 158, 11, 0.1) !important;
}

.dark .border-emerald-200 {
  border-color: rgba(16, 185, 129, 0.3) !important;
}

.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.dark .border-amber-200 {
  border-color: rgba(245, 158, 11, 0.3) !important;
}

.dark .border-emerald-800 {
  border-color: rgba(16, 185, 129, 0.5) !important;
}

.dark .border-red-800 {
  border-color: rgba(239, 68, 68, 0.5) !important;
}

.dark .border-amber-800 {
  border-color: rgba(245, 158, 11, 0.5) !important;
}

.dark .text-emerald-700 {
  color: #10b981 !important;
}

.dark .text-emerald-400 {
  color: #34d399 !important;
}

.dark .text-red-700 {
  color: #ef4444 !important;
}

.dark .text-red-400 {
  color: #f87171 !important;
}

.dark .text-amber-500 {
  color: #f59e0b !important;
}

/* Profile picture border */
.dark .border-white {
  border-color: #1e293b !important;
}

/* Button styling */
.dark .choose-file-btn {
  background-color: #06B6D4 !important;
}

.dark .choose-file-btn:hover {
  background-color: #0891B2 !important;
}

.dark .update-profile-btn {
  background-color: #06B6D4 !important;
}

.dark .update-profile-btn:hover:not(:disabled) {
  background-color: #0891B2 !important;
}

/* File input styling for dark mode */
.dark input[type="file"] {
  color: #f1f5f9 !important;
}

.dark input[type="file"]::-webkit-file-upload-button {
  background-color: #475569 !important;
  color: #f1f5f9 !important;
  border: 1px solid #64748b !important;
}

.dark input[type="file"]::file-selector-button {
  background-color: #475569 !important;
  color: #f1f5f9 !important;
  border: 1px solid #64748b !important;
}

/* Additional slate color overrides for comprehensive coverage */
.dark .bg-slate-800\/50 {
  background-color: rgba(30, 41, 59, 0.5) !important;
}

/* Ensure all text elements have proper contrast */
.dark h1,
.dark h2,
.dark h3,
.dark h4,
.dark h5,
.dark h6 {
  color: #f1f5f9 !important;
}

.dark p {
  color: #cbd5e1 !important;
}

.dark span {
  color: inherit !important;
}

/* Override any remaining white backgrounds */
.dark [class*="bg-white"] {
  background-color: #1e293b !important;
}

.dark [class*="bg-gray-50"] {
  background-color: #0f172a !important;
}

.dark [class*="bg-gray-100"] {
  background-color: #475569 !important;
}
</style>
